import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadConsentForFutureContactTypes,
    loadDukRiskScores,
    loadGenderSameAtBirthTypes,
    loadGpParticipationConsentTypes,
    loadIsIndividualOnLearningDisabilityRegisterTypes,
    loadIsIndividualOnSmiRegisterTypes
} from '../../../store/directusService';
import {
    clearNdppData,
    createNdppData,
    loadNdppData,
    updateNdppData
} from '../../../store/participantService';
import { getAge } from '../../../utils/dateFunctions';
import { getObjFromId } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    CONSENT_FOR_FUTURE_CONTACT_ERROR,
    DUK_RISK_SCORE_ERROR,
    GENDER_SAME_AT_BIRTH_ERROR,
    GP_PARTICIPATION_CONSENT_ERROR,
    IS_ON_LEARNING_DISABILITY_REGISTER_ERROR,
    IS_ON_SMI_REGISTER_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { regExps } from '../../../utils/formValidation/validationPatterns';
import { ADVISER, hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateNDPPData';

import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const _CREATE_MESSAGE = 'Participant NDPP Data details have been created';
const _UPDATE_MESSAGE = 'Participant NDPP Data details have been updated';
const _CONSENT_AGE = 80;

const NDPPData = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        genderSameAtBirthId: '',
        dukValue: '',
        dukRiskScoreId: '',
        referralReturnedIncomplete: '',
        completeReferralsReceipt: '',
        previousGdmDiagnosis: false,
        obesityPathwayInnovation: false,

        isOnLearningDisabilityRegisterId: '',
        isOnSmiRegisterId: '',
        consentForFutureContactId: '',
        gpParticipationConsentId: '',
        dateOfferedAltPathway: '',
        acceptedAltPathway: null,
        previousGdmDiagnosisDisable: false
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [showGpParticipationConsent, setShowGpParticipationConsent] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const genderSameAtBirthTypes = useSelector(
        (state) => state.entities.directusService.genderSameAtBirthTypes
    );
    const isIndividualOnLearningDisabilityRegisterTypes = useSelector(
        (state) => state.entities.directusService.isIndividualOnLearningDisabilityRegisterTypes
    );
    const isIndividualOnSmiRegisterTypes = useSelector(
        (state) => state.entities.directusService.isIndividualOnSmiRegisterTypes
    );
    const consentForFutureContactTypes = useSelector(
        (state) => state.entities.directusService.consentForFutureContactTypes
    );
    const gpParticipationConsentTypes = useSelector(
        (state) => state.entities.directusService.gpParticipationConsentTypes
    );
    const dukRiskScores = useSelector((state) => state.entities.directusService.dukRiskScores);
    const ndppData = useSelector((state) => state.entities.participantService.ndppData);
    const loadedNdppData = useSelector((state) => state.entities.participantService.loadedNdppData);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (!genderSameAtBirthTypes?.length) dispatch(loadGenderSameAtBirthTypes());
        if (!isIndividualOnLearningDisabilityRegisterTypes?.length)
            dispatch(loadIsIndividualOnLearningDisabilityRegisterTypes());
        if (!isIndividualOnSmiRegisterTypes?.length) dispatch(loadIsIndividualOnSmiRegisterTypes());
        if (!consentForFutureContactTypes?.length) dispatch(loadConsentForFutureContactTypes());
        if (!gpParticipationConsentTypes?.length) dispatch(loadGpParticipationConsentTypes());
        if (!dukRiskScores?.length) dispatch(loadDukRiskScores());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if ('id' in currentParticipant) {
            dispatch(loadNdppData(currentParticipant.id));

            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);

            setShowGpParticipationConsent(
                getAge(currentParticipant?.dateOfBirth, currentParticipant?.referralDate) >=
                    _CONSENT_AGE
            );
        }
    }, [currentParticipant?.id, roles]);

    useEffect(() => {
        if (!loadedNdppData) return;

        if (Object.keys(ndppData)?.length && ndppData?.participantId === currentParticipant?.id) {
            const updated = {
                ...ndppData,
                previousGdmDiagnosis: ndppData.previousGdmDiagnosisDisable
                    ? true
                    : ndppData.previousGdmDiagnosis
            };
            setNewEntry(updated);
            setInitialStateOnEntry(updated);
        }
    }, [ndppData, loadedNdppData, currentParticipant?.id]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _CREATE_MESSAGE || successMessage === _UPDATE_MESSAGE)
            setIsUpdateButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
        if (!genderSameAtBirthTypes?.length) return GENDER_SAME_AT_BIRTH_ERROR;
        if (!isIndividualOnLearningDisabilityRegisterTypes?.length)
            return IS_ON_LEARNING_DISABILITY_REGISTER_ERROR;
        if (!isIndividualOnSmiRegisterTypes?.length) return IS_ON_SMI_REGISTER_ERROR;
        if (!consentForFutureContactTypes?.length) return CONSENT_FOR_FUTURE_CONTACT_ERROR;
        if (!gpParticipationConsentTypes?.length) return GP_PARTICIPATION_CONSENT_ERROR;
        if (!dukRiskScores?.length) return DUK_RISK_SCORE_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const cleanUp = () => dispatch(clearNdppData());

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateDateOfferedAltPathway = (date) => {
        onUpdate('dateOfferedAltPathway', date);
        !date && onUpdate('acceptedAltPathway', null);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry, showGpParticipationConsent);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        newEntry?.id
            ? dispatch(updateNdppData(newEntry, _UPDATE_MESSAGE))
            : dispatch(
                  createNdppData(
                      { ...newEntry, id: null, participantId: currentParticipant.id },
                      _CREATE_MESSAGE
                  )
              );
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div>
            <div className={form.formWrapper}>
                <form onSubmit={onSubmit} data-testid="form_start_ndpp_data" className={form.form}>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="genderSameAtBirthId"
                                label="Gender Same at Birth"
                                placeholder="Select gender same at birth options..."
                                disabled={isDisabled}
                                menuItems={genderSameAtBirthTypes || []}
                                selectedId={newEntry.genderSameAtBirthId || ''}
                                selected={getObjFromId(
                                    genderSameAtBirthTypes,
                                    newEntry.genderSameAtBirthId
                                )}
                                onChange={(chosenId) => onUpdate('genderSameAtBirthId', chosenId)}
                            />
                            <TextInputField
                                id="dukValue"
                                label="DUK Value"
                                placeholder="Enter DUK value..."
                                disabled={isDisabled}
                                maxLength={2}
                                value={newEntry.dukValue}
                                error={errors.dukValue}
                                onChange={(e) =>
                                    onUpdate(
                                        'dukValue',
                                        e.target.value.replace(regExps.DIGITS_R, '')
                                    )
                                }
                            />
                            <SingleSelect
                                id="dukRiskScoreId"
                                label="DUK Risk Score"
                                placeholder="Select DUK risk score..."
                                disabled={isDisabled}
                                menuItems={dukRiskScores || []}
                                selectedId={newEntry.dukRiskScoreId || ''}
                                selected={getObjFromId(dukRiskScores, newEntry.dukRiskScoreId)}
                                onChange={(chosenId) => onUpdate('dukRiskScoreId', chosenId)}
                            />
                            <DateSelect
                                id="referralReturnedIncomplete"
                                label="Referral Returned as Incomplete"
                                disabled={isDisabled}
                                value={newEntry.referralReturnedIncomplete}
                                error={errors.referralReturnedIncomplete}
                                onDateChange={(date) =>
                                    onUpdate('referralReturnedIncomplete', date)
                                }
                            />
                            <DateSelect
                                id="completeReferralsReceipt"
                                label="Complete Referrals Receipt"
                                disabled={isDisabled}
                                value={newEntry.completeReferralsReceipt}
                                error={errors.completeReferralsReceipt}
                                onDateChange={(date) => onUpdate('completeReferralsReceipt', date)}
                            />
                            <RadioButtons
                                id="previousGdmDiagnosis"
                                label="Previous GDM Diagnosis"
                                disabled={isDisabled || newEntry.previousGdmDiagnosisDisable}
                                value={newEntry.previousGdmDiagnosis || false}
                                onChange={(option) => onUpdate('previousGdmDiagnosis', option)}
                            />
                            <RadioButtons
                                id="obesityPathwayInnovation"
                                label="Obesity Pathway Innovation Programme"
                                disabled={isDisabled}
                                value={newEntry.obesityPathwayInnovation || false}
                                onChange={(option) => onUpdate('obesityPathwayInnovation', option)}
                            />
                        </div>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="isOnLearningDisabilityRegisterId"
                                label="Is Individual on Learning Disability Register"
                                placeholder="Select on learning disability register option..."
                                disabled={isDisabled}
                                menuItems={isIndividualOnLearningDisabilityRegisterTypes || []}
                                selectedId={newEntry.isOnLearningDisabilityRegisterId || ''}
                                selected={getObjFromId(
                                    isIndividualOnLearningDisabilityRegisterTypes,
                                    newEntry.isOnLearningDisabilityRegisterId
                                )}
                                error={errors.isOnLearningDisabilityRegisterId}
                                onChange={(chosenId) =>
                                    onUpdate('isOnLearningDisabilityRegisterId', chosenId)
                                }
                            />
                            <SingleSelect
                                id="isOnSmiRegisterId"
                                label="Is Individual on SMI Register"
                                placeholder="Select on SMI register option..."
                                disabled={isDisabled}
                                menuItems={isIndividualOnSmiRegisterTypes || []}
                                selectedId={newEntry.isOnSmiRegisterId || ''}
                                selected={getObjFromId(
                                    isIndividualOnSmiRegisterTypes,
                                    newEntry.isOnSmiRegisterId
                                )}
                                error={errors.isOnSmiRegisterId}
                                onChange={(chosenId) => onUpdate('isOnSmiRegisterId', chosenId)}
                            />
                            <SingleSelect
                                id="consentForFutureContactId"
                                label="Consent for Future Contact"
                                placeholder="Select a consent for future contact option..."
                                disabled={isDisabled}
                                menuItems={consentForFutureContactTypes || []}
                                selectedId={newEntry.consentForFutureContactId || ''}
                                selected={getObjFromId(
                                    consentForFutureContactTypes,
                                    newEntry.consentForFutureContactId
                                )}
                                error={errors.consentForFutureContactId}
                                onChange={(chosenId) =>
                                    onUpdate('consentForFutureContactId', chosenId)
                                }
                            />
                            {showGpParticipationConsent && (
                                <SingleSelect
                                    id="gpParticipationConsentId"
                                    label="GP Participation Consent"
                                    placeholder="Select a GP participation consent option..."
                                    disabled={isDisabled}
                                    mandatory={showGpParticipationConsent}
                                    menuItems={gpParticipationConsentTypes || []}
                                    selectedId={newEntry.gpParticipationConsentId || ''}
                                    selected={getObjFromId(
                                        gpParticipationConsentTypes,
                                        newEntry.gpParticipationConsentId
                                    )}
                                    error={errors.gpParticipationConsentId}
                                    onChange={(chosenId) =>
                                        onUpdate('gpParticipationConsentId', chosenId)
                                    }
                                />
                            )}
                            <DateSelect
                                id="dateOfferedAltPathway"
                                label="Date Offered Alternative Pathway"
                                disabled={isDisabled}
                                value={newEntry.dateOfferedAltPathway}
                                error={errors.dateOfferedAltPathway}
                                onDateChange={(date) => onUpdateDateOfferedAltPathway(date)}
                            />
                            {newEntry.dateOfferedAltPathway && (
                                <RadioButtons
                                    id="acceptedAltPathway"
                                    label="Accepted Alternative Pathway"
                                    disabled={isDisabled}
                                    mandatory={!!newEntry.dateOfferedAltPathway}
                                    value={newEntry.acceptedAltPathway}
                                    error={errors.acceptedAltPathway}
                                    onChange={(option) => onUpdate('acceptedAltPathway', option)}
                                />
                            )}
                        </div>
                    </div>

                    <Button
                        id="ndppDataUpdateButton"
                        disabled={isDisabled || isUpdateButtonDisabled}
                        content="Update NDPP Data"
                        error={errors.button}
                        clearError={() => clearError('button')}
                    />
                </form>
            </div>
        </div>
    );
};

export default NDPPData;
